import { useState } from "react";
import AppDrawer from "../../../../components/Drawers/AppDrawer";
import { DrawerProps } from "../../../../components/Drawers/DrawerTypes";
import { Alert, Button, Input } from "reactstrap";
import BasicTableView from "../../CloudListView/BasicTableView";
import IconButton from "../../../../elements/IconButton";
import {
  emailRegex,
  emailValidationRegex,
} from "../../../../components/Drawers/EmailActionsDrawer/EmailActionsDrawer";
import useBulkAddUsers from "./useBulkAddUsers";
import pluralize from "pluralize";

interface Props extends DrawerProps {
  cloudID: string;
}

export default function BulkAddUsersDrawer({ open, onClose, cloudID }: Props) {
  const [text, setText] = useState("");
  const [recipients, setRecipients] = useState<string[]>([]);

  const {
    bulkAdd,
    bulkAdding,
    reset,
    finished,
    successEmails,
    failedEmails,
    error,
  } = useBulkAddUsers();

  const InputStage = (
    <>
      {error ? (
        <Alert
          color="danger"
          className="tw:-mt-4 tw:py-2 tw:px-4 tw:flex tw:items-center tw:font-medium"
        >
          <i className="mdi mdi-close-circle tw:text-xl tw:mr-2" />
          {error}
        </Alert>
      ) : (
        <Alert className="tw:py-2 tw:-mt-4 tw:font-medium" color="info">
          This feature is designed for bulk adding users to a Cloud. Users will
          not receive an email invite, and will be given the "Cloud Contributor"
          role by default.
        </Alert>
      )}
      <h4 className="mb-0">Enter Recipient Emails</h4>
      <small>Enter as many comma separated emails as you'd like.</small>
      <Input
        type="textarea"
        className="mt-2"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="bob@gmail.com,bill@gmail.com"
      />
      <Button
        color="primary"
        style={{ width: 100 }}
        className="mt-3 mb-4"
        onClick={addRecipients}
      >
        Add
      </Button>
      <BasicTableView
        items={recipients}
        loading={false}
        title={"Recipients"}
        getItemTitle={(r) => r}
        getItemSubtitle={() => ""}
        emptyMessage="No Recipients have been added."
        containerCSS="mb-4"
        CustomRenderer={(r) => (
          <IconButton
            icon="close"
            onClick={() => {
              setRecipients((prev) => prev.filter((x) => x !== r));
            }}
            color="danger"
          />
        )}
      />
    </>
  );

  const FinishedStage = (
    <>
      {successEmails.length > 0 && (
        <Alert
          color="success"
          className="tw:my-0 tw:py-2 tw:px-4 tw:flex tw:items-center tw:font-medium"
        >
          <i className="mdi mdi-check-circle tw:text-xl tw:mr-2" />{" "}
          {successEmails.length} {pluralize("user", successEmails.length)}{" "}
          successfully added.
        </Alert>
      )}
      {failedEmails.length > 0 && (
        <>
          <Alert
            color="danger"
            className="tw:mt-4 tw:py-2 tw:px-4 tw:flex tw:items-center tw:font-medium"
          >
            <i className="mdi mdi-close-circle tw:text-xl tw:mr-2" />{" "}
            {failedEmails.length} {pluralize("user", failedEmails.length)}{" "}
            failed to be added.
          </Alert>
          <BasicTableView
            items={failedEmails}
            loading={false}
            title={"Failed Recipients"}
            getItemTitle={(r) => r.email}
            getItemSubtitle={(r) => r.reason}
            emptyMessage="No Recipients have been added."
            containerCSS="mb-4"
          />
        </>
      )}
    </>
  );

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerOptions={
        finished ? (
          <>
            <Button
              color="secondary"
              onClick={handleClose}
              size="sm"
              className="tw:px-4"
            >
              Close
            </Button>
            <Button color="primary" onClick={handleReset} size="sm">
              Start Over
            </Button>
          </>
        ) : (
          <Button
            onClick={bulkAddUsers}
            disabled={!recipients?.length || bulkAdding}
            className="tw:w-30"
            size="sm"
            color="primary"
          >
            {bulkAdding ? "Adding users..." : "Add Users"}
          </Button>
        )
      }
      body={
        <>
          {!finished && InputStage}
          {finished && FinishedStage}
        </>
      }
      headerTitle={"Add Users To Cloud"}
    />
  );

  function addRecipients() {
    const emails = text.match(emailRegex);

    if (!emails) return;

    const validEmails = emails.filter((email) => {
      return emailValidationRegex.test(email);
    });

    setRecipients((prev) => [...new Set([...prev, ...validEmails])]);
    setText("");
  }

  async function bulkAddUsers() {
    if (!recipients?.length) return;

    bulkAdd({
      cloudID: cloudID,
      emails: recipients,
      sendEmailInvite: false,
      defaultPermissionID: undefined,
    });
  }

  function handleReset() {
    setRecipients([]);
    setText("");
    reset();
  }

  function handleClose() {
    onClose();
    handleReset();
  }
}
